import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  VpnKey as AnswerKeyIcon,
} from '@mui/icons-material';
import usePdfExport from '@/hooks/usePdfExport';

/**
 * Reusable PDF export button.
 *
 * @param {Object} props
 * @param {() => Promise<void>} props.onExport       - Called when user clicks "Export PDF"
 * @param {() => Promise<void>} [props.onExportWithAnswers] - Called when admin clicks "Export with Answers"
 * @param {boolean}  [props.isAdmin=false]  - Show answer-key option
 * @param {string}   [props.variant]        - MUI Button variant (default: "outlined")
 * @param {string}   [props.size]           - MUI Button size (default: "small")
 * @param {boolean}  [props.iconOnly=false] - Render as IconButton instead of full Button
 * @param {string}   [props.label]          - Custom label text
 * @param {Object}   [props.sx]             - Additional styles
 * @param {string}   [props.color]          - Button color
 * @param {boolean}  [props.disabled]       - Externally disable the button
 */
const ExportPdfButton = ({
  onExport,
  onExportWithAnswers,
  isAdmin = false,
  variant = 'outlined',
  size = 'small',
  iconOnly = false,
  label = 'Export PDF',
  sx = {},
  color = 'primary',
  disabled = false,
}) => {
  const { exporting, exportPdf } = usePdfExport();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleClick = (event) => {
    if (isAdmin && onExportWithAnswers) {
      setAnchorEl(event.currentTarget);
    } else {
      exportPdf(onExport);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportNormal = () => {
    handleClose();
    exportPdf(onExport);
  };

  const handleExportWithAnswers = () => {
    handleClose();
    exportPdf(onExportWithAnswers);
  };

  if (iconOnly) {
    return (
      <>
        <Tooltip title={exporting ? 'Generating PDF...' : label}>
          <span>
            <IconButton
              size={size}
              color={color}
              onClick={handleClick}
              disabled={disabled || exporting}
              sx={sx}
            >
              {exporting ? <CircularProgress size={20} /> : <PdfIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {isAdmin && onExportWithAnswers && (
          <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleClose}>
            <MenuItem onClick={handleExportNormal}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export PDF</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleExportWithAnswers}>
              <ListItemIcon>
                <AnswerKeyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Export with Answer Key</ListItemText>
            </MenuItem>
          </Menu>
        )}
      </>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        color={color}
        startIcon={exporting ? <CircularProgress size={16} /> : <PdfIcon />}
        onClick={handleClick}
        disabled={disabled || exporting}
        sx={{
          textTransform: 'none',
          borderRadius: 2,
          fontWeight: 600,
          ...sx,
        }}
      >
        {exporting ? 'Generating...' : label}
      </Button>
      {isAdmin && onExportWithAnswers && (
        <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleClose}>
          <MenuItem onClick={handleExportNormal}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportWithAnswers}>
            <ListItemIcon>
              <AnswerKeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export with Answer Key</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default ExportPdfButton;
